/* Les motifs, l'accessibilité, la langue, le téléphone, la gouvernance.
 *
 * C'est la partie qu'on saute quand on fabrique une charte pour la montrer, et
 * celle qu'on cherche quand on en a une à appliquer. Elle dit ce qu'il faut
 * faire dans les cas qui reviennent — un écran vide, une erreur, un chargement
 * — et surtout qui décide quand une valeur manque.
 */

import { Banner, Empty, Icon, Section, Tag } from "./reexports";
import { A11y, BlocP, BlocT, Demo, Regle } from "./briques";

export function Pratiques() {
  return (
    <>
      <Section titre="Motifs" note="les cas qui reviennent, résolus une fois">
        <div className="card card-pad">
          <BlocT note="trois formes, pas une de plus">Formes d'écran</BlocT>
          <div className="regles">
            <Regle titre="La liste">
              Des indicateurs en haut, un filtre, un tableau. Le clic sur une ligne ouvre la
              fiche ; l'action de création est un bouton dans le rail, pas dans la page — on crée
              plus souvent qu'on ne consulte une liste précise.
            </Regle>
            <Regle titre="La fiche">
              Un en-tête qui porte l'identité et les actions, un parcours s'il y a lieu, puis des
              sections. Les onglets servent quand les sections dépassent trois écrans.
            </Regle>
            <Regle titre="La saisie">
              Un en-tête de document, des lignes, une barre d'action qui reste visible. Le total
              s'affiche sur le bouton d'enregistrement : on valide un montant, pas un formulaire.
            </Regle>
          </div>

          <BlocT>Les trois états d'une page</BlocT>
          <BlocP>
            Un écran qui n'a rien à montrer doit quand même dire pourquoi, et surtout quoi faire.
            Le vide par défaut — une page blanche — est le seul interdit.
          </BlocP>
          <Demo quoi="vide" note="dit l'action qui remplit l'écran">
            <Empty
              titre="Devis vide"
              detail="Ouvrez le catalogue pour cocher les références à mettre au devis."
            />
          </Demo>
          <Demo quoi="en attente" note="on annonce ce qui arrive, pas un sablier">
            <div className="chargement">Chargement…</div>
          </Demo>
          <Demo quoi="refus" note="la cause, puis la suite">
            <Banner>
              Trois références partiront au plancher : une demande de baisse suivra, et c'est elle
              qui ouvrira la porte.
            </Banner>
          </Demo>
        </div>
      </Section>

      <Section titre="Accessibilité" note="ce qui est tenu, et par quoi">
        <div className="card card-pad regles">
          <Regle titre="Le contraste est mesuré, pas estimé">
            Onze paires de couleurs, dans les deux thèmes, vérifiées contre la feuille de style
            elle-même à chaque livraison. Une teinte qui passe sous 4,5 fait échouer la
            construction — c'est ainsi qu'a été rattrapé l'orangé des étiquettes « en attente »,
            qui donnait 4,31.
          </Regle>
          <Regle titre="Le focus ne se retire jamais">
            Un anneau de 2 px en <code>--accent</code>, posé une fois pour toute la feuille sur
            <code> :focus-visible</code>. Il n'apparaît qu'au clavier, donc rien n'empêche de le
            garder.
          </Regle>
          <Regle titre="La couleur ne porte jamais seule">
            Chaque état porte son mot. Les étiquettes se lisent en niveaux de gris, les barres
            portent leur valeur écrite, les pastilles de filtre portent <code>aria-pressed</code>.
          </Regle>
          <Regle titre="Le mouvement se coupe">
            <code>prefers-reduced-motion</code> arrête toute animation et toute transition, en une
            règle, pour toute l'application.
          </Regle>
          <Regle titre="La cible tient sous le doigt">
            Les commandes font au moins 34 px de côté au téléphone, et les champs 16 px de texte —
            en dessous, iOS zoome et la page se dérobe.
          </Regle>
        </div>
      </Section>

      <Section titre="Langue" note="l'interface parle, elle n'étiquette pas">
        <div className="card card-pad regles">
          <Regle titre="Phrases, pas codes">
            « 3 cartons de 20 — 60 unités × 7,50 € » se vérifie d'un coup d'œil ; « 60 » ne se
            vérifie pas. Partout où un calcul produit un chiffre, la phrase qui l'explique
            l'accompagne.
          </Regle>
          <Regle titre="Le refus nomme sa cause">
            Jamais « action impossible ». Toujours ce qui manque : « sans coût envoyé, il n'y a
            rien à faire designer ».
          </Regle>
          <Regle titre="Pas de jargon d'implémentation">
            Ni « RLS », ni « migration », ni « null ». L'écran dit « réservé à la direction »,
            « vide », « pas encore renseigné ».
          </Regle>
          <Regle titre="Les nombres à la française">
            Virgule décimale, espace insécable pour les milliers, euro après le montant. Les dates
            en 28/10/2026 ; les durées en clair, comptées en minuits et non en tranches de
            24 heures — « hier » veut dire hier.
          </Regle>
          <Regle titre="Le français jusque dans le code">
            Les noms de variables, de colonnes et de fonctions sont en français. Un dossier de
            packaging s'appelle <code>dossier</code>, pas <code>packagingCase</code> : on parle du
            métier dans sa langue, et la traduction mentale disparaît.
          </Regle>
        </div>
      </Section>

      <Section titre="Téléphone" note="390 px, la contrainte qui décide">
        <div className="card card-pad regles">
          <Regle titre="Rien ne déborde">
            La largeur de référence est 390 px. Un écran qui dépasse se consulte en poussant la
            page de côté : le défaut le plus courant et le plus silencieux. Une passe automatique
            mesure chaque écran à cette largeur avant chaque livraison, panneaux ouverts compris.
          </Regle>
          <Regle titre="Une table trop large devient des blocs">
            Sous 560 px, les colonnes secondaires disparaissent, et les tables denses se replient
            en blocs qui portent leur étiquette. On ne rétrécit pas, on repense.
          </Regle>
          <Regle titre="Le rail devient barre d'onglets">
            Quatre destinations en bas, le reste derrière « Plus ». Les pastilles de filtre
            deviennent une liste déroulante : les deux sont rendues, la CSS n'en montre qu'une.
          </Regle>
        </div>
      </Section>

      <Section titre="Gouvernance" note="qui décide quand une valeur manque">
        <div className="card card-pad">
          <div className="regles">
            <Regle titre="Une valeur absente de l'échelle n'existe pas">
              Si le rayon qu'il faut n'est pas dans la liste, deux issues seulement : prendre le
              plus proche, ou ajouter une marche — et alors l'ajouter pour tout le monde, avec sa
              raison écrite à côté. La marche <code>--nuit-850</code> est là pour ça : elle existe
              parce que le fond sombre l'exigeait, et la raison tient en une ligne de commentaire.
            </Regle>
            <Regle titre="Un composant nouveau documente ses états">
              Repos, survol, focus, désactivé, en cours, en erreur. Un composant dont les états
              ne sont pas montrés est un composant dont personne ne sait à quoi ressemble le
              refus — et c'est le refus qu'on voit le plus souvent en production.
            </Regle>
            <Regle titre="La charte est éprouvée, pas relue">
              Cinq épreuves refusent une taille, un rayon, une profondeur, une ombre ou une durée
              écrits en dur hors de la couche de jetons. Une sixième refuse qu'une couleur crue
              apparaisse dans une règle. Une septième vérifie que les deux blocs du thème sombre
              ne divergent pas. Tant que la contrainte n'est pas mécanique, l'échelle n'est qu'une
              suggestion — et une suggestion se perd en trois semaines.
            </Regle>
            <Regle titre="La dette se compte et se verrouille">
              Les écarts d'espacement hérités sont comptés, et le compte ne peut que descendre.
              Un système qui prétend être appliqué partout alors qu'il ne l'est pas enseigne à
              l'ignorer ; un système qui annonce sa dette et la rend décroissante s'applique.
            </Regle>
          </div>

          <BlocT>Ce que vérifie la machine</BlocT>
          <div className="tablewrap">
            <table className="api">
              <thead>
                <tr><th>Épreuve</th><th>Refuse</th></tr>
              </thead>
              <tbody>
                {[
                  ["aucune taille de police en dur", "font-size: 13.5px"],
                  ["aucun rayon en dur", "border-radius: 9px"],
                  ["aucune profondeur en dur", "z-index: 41"],
                  ["aucune ombre à la main", "box-shadow: 0 2px 6px …"],
                  ["aucune durée à la main", "transition: … .18s"],
                  ["aucune couleur crue dans une règle", "color: #5A6660"],
                  ["les deux thèmes sombres ne divergent pas", "une correction faite d'un seul côté"],
                  ["onze paires de contraste, deux thèmes", "une teinte sous 4,5"],
                  ["la dette d'espacement ne monte pas", "un écart hors échelle de plus"],
                ].map(([q, r]) => (
                  <tr key={q}>
                    <td>{q}</td>
                    <td><code className="api-t">{r}</code></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <A11y>
            Ces épreuves tournent avec les autres : <code>npm test</code>. Rien ne part en ligne
            sans elles, et aucune ne demande à qui que ce soit de regarder un écran.
          </A11y>

          <BlocT>Ce qui reste à reprendre</BlocT>
          <BlocP>
            L'espacement, marche par marche, au fil des écrans qu'on touche. La couleur d'usine,
            qui est une donnée et non une décision de charte, mais dont rien ne garantit
            aujourd'hui qu'elle contraste avec le blanc de sa tuile. Et les six paires d'états en
            thème sombre, réglées à la main plutôt que tirées d'une rampe — elles tiennent leurs
            seuils, mais elles ne descendent de rien.
          </BlocP>
          <div className="rang">
            <Tag ton="warn" dot>espacement : en cours</Tag>
            <Tag ton="warn" dot>contraste des couleurs d'usine</Tag>
            <Tag ton="info">états sombres : hors rampe</Tag>
          </div>
        </div>
      </Section>

      <div className="charte-pied">
        <Icon n="info" cls="" />
        <span>
          Cette page est rendue avec la feuille de style de l'application et lit ses variables au
          moment de l'affichage. Elle ne décrit pas la charte : elle l'exécute.
        </span>
      </div>
    </>
  );
}
